import Marker from '@rio-cloud/rio-uikit/Marker';
import type { MapApi } from '@rio-cloud/rio-uikit/mapTypes';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useIntl } from 'react-intl';

import { formatTourStopMarkerLabel, TourStopSequenceMarker } from '#components/tourStops/TourStopSequenceMarker.tsx';
import {
    getTourStopCollisionGroups,
    type TourStopCollisionGroup,
} from '#features/tourCreate/utils/tourStopMarkerCollision.utils.ts';
import type { TourStopMapItem } from '#features/tourCreate/utils/tourStopsMap.utils.ts';

type TourStopMarkersProps = {
    api: MapApi;
    items: TourStopMapItem[];
};

export type TourStopMarkersHandle = {
    recalculate: () => void;
    resetForMapMovement: () => void;
};

const INDICATOR_OFFSET = { x: 16, y: -42 };

export const TourStopMarkers = forwardRef<TourStopMarkersHandle, TourStopMarkersProps>(({ api, items }, ref) => {
    const intl = useIntl();
    const [collisionGroups, setCollisionGroups] = useState<TourStopCollisionGroup[]>([]);

    const recalculate = useCallback(() => {
        const map = api.map;
        if (!map) {
            setCollisionGroups([]);
            return;
        }

        setCollisionGroups(getTourStopCollisionGroups(items, position => map.geoToScreen(position)));
    }, [api.map, items]);

    useEffect(() => {
        recalculate();
    }, [recalculate]);

    useImperativeHandle(
        ref,
        () => ({
            recalculate,
            resetForMapMovement: () => setCollisionGroups([]),
        }),
        [recalculate]
    );

    const hiddenItemIds = new Set(collisionGroups.flatMap(group => group.slice(1).map(item => item.id)));

    return (
        <>
            {items
                .filter(item => !hiddenItemIds.has(item.id))
                .slice()
                .reverse()
                .map(item => (
                    <Marker
                        key={`${item.id}-${item.sequence}-${item.role}`}
                        position={item.position}
                        icon={
                            <TourStopSequenceMarker
                                ariaLabel={formatTourStopMarkerLabel(intl, item)}
                                sequence={item.sequence}
                                role={item.role}
                            />
                        }
                    />
                ))}
            {collisionGroups.map(group => {
                const map = api.map;
                if (!map) {
                    return null;
                }
                const anchorPoint = map.geoToScreen(group[0].position);
                const indicatorPosition = map.screenToGeo(
                    anchorPoint.x + INDICATOR_OFFSET.x,
                    anchorPoint.y + INDICATOR_OFFSET.y
                );
                const hiddenStopLabels = group
                    .slice(1)
                    .map(item =>
                        intl.formatMessage(
                            {
                                id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.hidden-marker',
                            },
                            { sequence: item.sequence }
                        )
                    )
                    .join('\n');
                return (
                    <Marker
                        key={`indicator-${group[0].id}`}
                        position={indicatorPosition}
                        icon={
                            <span title={hiddenStopLabels} className='badge badge-muted'>
                                +{group.length - 1}
                            </span>
                        }
                    />
                );
            })}
        </>
    );
});

TourStopMarkers.displayName = 'TourStopMarkers';
